import { NavLink } from 'react-router-dom';
import CustomGrid from '../components/CustomGrid.js';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api/axiosConfig';
import { Box, Typography, CircularProgress } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { Select, MenuItem, Checkbox } from '@mui/material';

// 컬럼정의
const initialColumns: GridColDef[] = [
    {
        field: 'rowNumber',
        headerName: 'NO',
        width: 60,
        sortable: false,
        filterable: false,

        renderCell: (params) => {
            const { page, pageSize } = params.api.state.pagination.paginationModel;
            const rowIds = params.api.getAllRowIds();
            const globalIndex = rowIds.indexOf(params.id);
            return globalIndex + 1;
        },
    },
    {
        field: 'CUSTOMER_NAME',
        headerName: '거래처명',
        flex: 2,
        editable: true,
        align: 'left',
        headerAlign: 'center',
        headerClassName: 'required-header-field',
    },
    {
        field: 'BANK',
        headerName: '은행',
        flex: 0.8,
        editable: true,
        align: 'center',
        headerAlign: 'center',
    },
    {
        field: 'BANK_ACCOUNT',
        headerName: '계좌',
        flex: 1,
        editable: true,
        align: 'left',
        headerAlign: 'center',
    },
    {
        field: 'ACCOUNT_HOLDER',
        headerName: '예금주',
        flex: 1.5,
        editable: true,
        align: 'center',
        headerAlign: 'center',
    },
    {
        field: 'IN_OUT',
        headerName: '매입매출',
        flex: 0.8,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        renderEditCell: (params) => {
            const handleChange = (event) => {
                params.api.setEditCellValue({
                    id: params.id,
                    field: params.field,
                    value: event.target.value
                });
            };

            return (
                <Select
                    value={params.value || ''}
                    onChange={handleChange}
                    fullWidth
                    autoFocus
                    sx={{ height: '100%' }}
                >
                    <MenuItem value="매입">매입</MenuItem>
                    <MenuItem value="매출">매출</MenuItem>
                    <MenuItem value=""></MenuItem>
                </Select>
            );
        },
    },
   {
       field: 'DIVISION',
       headerName: '구분',
       flex: 0.8,
       editable: true,
       align: 'center',
       headerAlign: 'center',
       renderEditCell: (params) => {
           const handleChange = (event) => {
               params.api.setEditCellValue({
                   id: params.id,
                   field: params.field,
                   value: event.target.value
               });
           };

           return (
               <Select
                   value={params.value || ''}
                   onChange={handleChange}
                   fullWidth
                   autoFocus
                   sx={{ height: '100%' }}
               >
                   <MenuItem value="장비">장비</MenuItem>
                   <MenuItem value="자재외">자재외</MenuItem>
                   <MenuItem value=""></MenuItem>
               </Select>
           );
       },
   },
    {
        field: 'ETC_YN',
        headerName: '기타경비',
        flex: 0.5,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
            <Checkbox checked={params.value === 'Y'} disabled />
        ),
        renderEditCell: (params) => (
            <Checkbox
                checked={params.value === 'Y'}
                onChange={(e) => {
                    params.api.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: e.target.checked ? 'Y' : 'N'
                    });
                }}
            />
        ),
    },
    {
        field: 'C_CNT',
        headerName: '거래횟수',
        type: 'number',
        width: 100,
        align: 'right',
        headerAlign: 'center',
    },
    {
        field: 'CREATED_AT',
        headerName: '최종수정일',
        type: 'text',
        width: 180,
        align: 'center',
        headerAlign: 'center',
    },
];

const areRowsEqual = (row1, row2) => {
    const keys1 = Object.keys(row1).filter(k => k !== 'id' && k !== 'isNew');
    const keys2 = Object.keys(row2).filter(k => k !== 'id' && k !== 'isNew');

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) { 
        if (String(row1[key]) !== String(row2[key])) {
            return false;
        }
    }
    return true;
};


function TestPage2() {
    const [dataRows, setDataRows] = useState([]);
    const [originalRows, setOriginalRows] = useState([]);

    const [dataColumns, setDataColumns] = useState(initialColumns);
    const [loading, setLoading] = useState(true);
    const [customerId, setCustomerId] = useState("");
    const [selectedRows, setSelectedRows] = useState([]); //체크된행

    const getSelectedRowsData = useCallback(() => {
        return dataRows.filter(row => selectedRows.includes(row.id));
    }, [dataRows, selectedRows]);


    const handleCustomerIdChange = useCallback((newId) => {
        setCustomerId(newId);

    }, [setCustomerId]);


    const searchList = useCallback(() => {
        setLoading(true);

        const params = new URLSearchParams();
        if (customerId) {
            params.append('customerId', customerId);
        }

        api
            .get(`/api/customer/searchList?${params.toString()}`)
            .then(res => {
                const data = res.data || [];

                const transformedData = data.map(item => ({
                    ...item,
                    id: item.CUSTOMER_ID,
                    isNew: false,
                }));

                setDataRows(transformedData);
                setOriginalRows(transformedData);
            })
            .catch(err => {
                console.error("데이터 로드 실패:", err);
                setDataRows([]); // 에러 발생 시 빈 배열로 초기화
            })
            .finally(() => {
                setLoading(false);
            });
    }, [customerId]);

    useEffect(() => {
        searchList();
    }, [searchList]);


    // 행추가
    const onAddRow = useCallback(() => {
        const tempId = `new-row-${Date.now()}-${Math.random()}`;
        const newRow = {
            CUSTOMER_ID: tempId,
            id: tempId,
            CUSTOMER_NAME: '',
            isNew: true,
        };

        setDataRows(prevRows => [newRow, ...prevRows]);

    }, []);


    // 저장 
    const onSave = useCallback(() => {

        if (!window.confirm('저장하시겠습니까?')) return;

        let sendData = {
            createdList: [],
            updatedList: []
        };


        for (const currentRow of dataRows) {
            // 거래처명 입력 체크
            if (currentRow.CUSTOMER_NAME === "" || currentRow.CUSTOMER_NAME === undefined) {
                window.alert('거래처명을 입력해주세요.')
                return false;
            }

            if (currentRow.isNew === true) {
                sendData.createdList.push(currentRow);
            } else {
                const originalRow = originalRows.find(
                    r => r.CUSTOMER_ID === currentRow.CUSTOMER_ID
                );

                if (originalRow && !areRowsEqual(originalRow, currentRow)) {
                    sendData.updatedList.push(currentRow);
                }
            }

        }

        if (sendData.createdList.length === 0 && sendData.updatedList.length === 0) {
            window.alert('변경사항이 없습니다.')
            return;
        }

        api
            .post(`/api/customer/save`, sendData)
            .then(() => {
                alert('저장되었습니다.');
                searchList();
            })
            .catch(err => {
                alert('저장 중 오류가 발생했습니다. ' + (err.response?.data || err.message));
            });


    }, [dataRows, originalRows]);


    // 체크된 행 얻기
    const handleSelectionChange = useCallback((newSelectionModel) => {
        const idsSet = newSelectionModel?.ids;

        if (idsSet) {
            const newIdsArray = Array.from(idsSet);
            setSelectedRows(newIdsArray);
        } else {
            setSelectedRows(newSelectionModel);
        }
    }, []);

    // 삭제
    const onDelete = useCallback(() => {

        if (!window.confirm('삭제하시겠습니까?')) return;

        const rowsToDelete = dataRows.filter(row => selectedRows.includes(row.id));

        if (rowsToDelete.length === 0) {
            window.alert('삭제할 행을 선택해주세요.');
            return;
        }

        const dbDeleteList = rowsToDelete
            .filter(row => !row.isNew)
            .map(row => ({
                CUSTOMER_ID: row.CUSTOMER_ID
            }));

        const clientOnlyDeleteIds = rowsToDelete
            .filter(row => row.isNew)
            .map(row => row.id);

        if (dbDeleteList.length > 0) {
            api
                .post(`/api/customer/delete`, dbDeleteList)
                .then(() => {
                    alert('삭제되었습니다.');
                    searchList();
                    setSelectedRows([]);
                })
                .catch(err => {
                    alert('삭제 중 오류가 발생했습니다. ' + (err.response?.data || err.message));
                });
        }

        if (clientOnlyDeleteIds.length > 0 || dbDeleteList.length === 0) {

            setDataRows(prevRows =>
                prevRows.filter(row =>
                    !rowsToDelete.some(deletedRow => deletedRow.id === row.id)
                )
            );

            if (dbDeleteList.length === 0) {
                alert('삭제되었습니다.');
            }

            setSelectedRows([]); // 선택 상태 초기화
        }


    }, [searchList, dataRows, selectedRows]);

    // 신규 행 스타일 변경
    const getRowClassName = useCallback((params) => {
        if (params.row.isNew === true) {
            return 'new-row-highlight';
        }

        return '';
    }, []);

    // 수정된내용 반영
    const handleProcessRowUpdate = useCallback((newRow, oldRow) => {

        let updatedRow = { ...newRow };

        // 거래처명 변경 시 예금주에 자동 반영
        if (newRow.CUSTOMER_NAME !== oldRow.CUSTOMER_NAME) {
            updatedRow.ACCOUNT_HOLDER = newRow.CUSTOMER_NAME;
        }

        setDataRows(prevRows =>
            prevRows.map(row =>
                row.id === updatedRow.id ? updatedRow : row
            )
        );

        return updatedRow;
    }, []);

    // 에러 핸들링 추가
    const handleProcessRowUpdateError = useCallback((error) => {
        console.error('Row update error:', error);
    }, []);


    return (
        <div >
            <CustomGrid
                dataRows={dataRows}
                dataColumns={dataColumns}
                addRowBtn={true}
                onAddRow={onAddRow}
                saveBtn={true}
                onSave={onSave}
                deleteBtn={true}
                onDelete={onDelete}
                onFieldSearch={false}
                onCustomerSearch={true}
                getRowClassName={getRowClassName}
                processRowUpdate={handleProcessRowUpdate}
                onFieldIdChange={''}
                onCustomerIdChange={handleCustomerIdChange}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                onSearch={searchList}

                onRowSelectionModelChange={handleSelectionChange}

                title="거래처 등록"
            />

        </div>
    );
}

export default TestPage2;